import { onMessage } from 'firebase/messaging';
import queryString from 'query-string';
import { matchPath } from 'react-router';

import { eventChannel } from 'redux-saga';
import { takeLatest, put, call, take, fork, race, select, join } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';

import { getPermissionDeniedKey } from 'constants/Global';
import { PATH } from 'constants/RoutesConstants';

import { APPLICATION_IS_READY } from 'modules/app/actionTypes';

import { getPage } from 'modules/notifications/selectors/ui/dashboard';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { navigate } from 'modules/router/navigate';
import { createNotification } from 'modules/toast-notifications/actions';
import { patchUser } from 'modules/users/actions';
import {
  getCurrentUserId,
  isImpersonatingUser,
  getUserNotificationConfig,
  getCurrentUserAdditionalData,
} from 'modules/users/selectors/currentUser';

import * as actions from './actions';
import * as types from './actionTypes';
import { CHANNELS } from './constants';
import firebase from '../../firebase';

import { getFleetbackSWRegistration } from '../../serviceWorker/runtime';
import { applicationIsReady } from '../app/selectors';

function* locationChanged(action) {
  yield join([yield fork(startDashboardApplication, action), yield fork(markAsReadAfterNavigation, action)]);
}

function* startDashboardApplication({ payload }) {
  const match = matchPath(PATH.USER_NOTIFICATIONS.path, payload.location.pathname);
  if (match) {
    const isReady = yield select(applicationIsReady);
    if (!isReady) {
      yield take(APPLICATION_IS_READY);
    }

    const userId = yield select(getCurrentUserId);
    yield put(actions.resetNotificationsDashboard());

    const page = yield select(getPage);
    yield put(
      actions.fetchNotificationsDashboard({
        userId,
        page,
      })
    );
  }
}

function* markAsReadAfterNavigation({ payload }) {
  const { location } = payload;

  const params = queryString.parse(location.search);
  if (params.notificationId) {
    const isReady = yield select(applicationIsReady);
    if (!isReady) {
      yield take(APPLICATION_IS_READY);
    }

    const userId = yield select(getCurrentUserId);
    const isImpersonating = yield select(isImpersonatingUser);

    if (!isImpersonating) {
      yield put(actions.markAsRead({ userId, notificationId: params.notificationId }));
      const { type } = yield take([types.MARK_AS_READ_SUCCESS, types.MARK_AS_READ_FAILURE]);

      if (type === types.MARK_AS_READ_SUCCESS) {
        const { notificationId: _toRemove, ...newParams } = params;
        const newPath = queryString.stringifyUrl({
          url: location.pathname,
          query: newParams,
        });
        yield call(navigate, newPath, { replace: true, state: location.state });
      }
    }
  }
}

function createChannel() {
  return eventChannel((emitter) => {
    navigator.serviceWorker.addEventListener('message', emitter);

    return () => {
      window.removeEventListener('message', emitter);
    };
  });
}

function* listen() {
  const channel = yield call(createChannel);

  while (true) {
    const { message, unsubscription } = yield race({
      message: take(channel),
      unsubscription: take(types.UNSUBSCRIBE_REQUEST),
    });

    if (unsubscription) {
      channel.close();
      break;
    } else {
      const { fcmMessageId, messageId, data } = message?.data ?? {};
      if ((fcmMessageId || messageId) && data) {
        const parsedData = Object.entries(data).reduce((acc, [key, value]) => {
          let parsedValue;
          try {
            parsedValue = JSON.parse(value);
          } catch {
            parsedValue = value;
          }

          return {
            ...acc,
            [key]: parsedValue,
          };
        }, {});
        yield put(actions.add([parsedData]));
      }
    }
  }
}

function* listenFocusMessage() {
  // Played when the browser tab is focused
  yield call(onMessage, firebase.messaging, async ({ notification }) => {
    if (notification) {
      const register = await navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope');
      register.showNotification(notification.title, {
        body: notification.body,
        ...notification,
        tag: notification.body,
      });
    }
  });
}

/** @param {import('./types').SubscribeAction} action */
function* subscribe({ payload, meta }) {
  const { request, userId } = payload;

  try {
    const serviceWorkerRegistration = yield call(getFleetbackSWRegistration);
    if (serviceWorkerRegistration.active) {
      const token = yield call(firebase.getToken, firebase.messaging, { serviceWorkerRegistration });

      yield call(restClient.request, {
        ...request,
        data: { token },
      });

      yield fork(listen);
      yield call(listenFocusMessage);

      yield put(actions.subscribeSuccess({ userId, token }, meta));
    } else {
      yield call(subscribe, { payload, meta });
    }
  } catch (error) {
    yield put(actions.subscribeFailure({ userId, error }, meta));
  }
}

/** @param {import('./types').UnsubscribeAction} action */
function* unsubscribe({ payload }) {
  const { request } = payload;

  try {
    yield call(restClient.request, request);
    yield put(actions.unsubscribeSuccess());
  } catch (error) {
    yield put(actions.unsubscribeFailure(error));
  }
}

/** @param {import('./types').FetchNotificationsAction} action */
function* fetchNotifications({ type, payload }) {
  const { request } = payload;
  const { params } = request;

  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.genericFetchNotificationsSuccess(type)(data, { params }));
  } catch (error) {
    yield put(actions.genericFetchNotificationsFailure(type)(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'notifications.navigation.notifications.patch.error',
          defaultMessage: 'An error has occurred during the settings notification update',
        },
      })
    );
  }
}

/** @param {import('./types').FetchUnreadNotificationsCountAction} action */
function* fetchUnreadNotificationsCount({ payload }) {
  const { request } = payload;
  const impersonate = yield select(isImpersonatingUser);

  if (!impersonate) {
    try {
      const { data } = yield call(restClient.request, request);
      yield put(actions.fetchUnreadNotificationsCountSuccess(data));
    } catch (error) {
      yield put(actions.fetchUnreadNotificationsCountFailure(error));
    }
  }
}

/** @param {import('./types').DeleteNotificationAction} action */
function* deleteNotification({ payload }) {
  const { request, notificationId } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.deleteNotificationSuccess(notificationId));
  } catch (error) {
    yield put(actions.deleteNotificationFailure(error, notificationId));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'notifications.navigation.notifications.patch.error',
          defaultMessage: 'An error has occurred during the settings notification update',
        },
      })
    );
  }
}

/** @param {import('./types').MarkAsReadAction} action */
function* markAsRead({ payload }) {
  const { request, notificationId } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.markAsReadSuccess(notificationId));
  } catch (error) {
    yield put(actions.markAsReadFailure(error, notificationId));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'notifications.navigation.notifications.patch.error',
          defaultMessage: 'An error has occurred during the settings notification update',
        },
      })
    );
  }
}

/** @param {import('./types').MarkAsUnreadAction} action */
function* markAsUnread({ payload }) {
  const { request, notificationId } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.markAsUnreadSuccess(notificationId));
  } catch (error) {
    yield put(actions.markAsUnreadFailure(error, notificationId));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'notifications.navigation.notifications.patch.error',
          defaultMessage: 'An error has occurred during the settings notification update',
        },
      })
    );
  }
}

/** @param {import('./types').MarkAllAsReadAction} action */
function* markAllAsRead({ payload }) {
  const { request } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.markAllAsReadSuccess());
  } catch (error) {
    yield put(actions.markAllAsReadFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.mark_all_read.failure',
          defaultMessage: 'Can’t mark everything as read',
        },
      })
    );
  }
}

/** @param {import('./types').FetchNotificationsConfigurationAction} action */
function* fetchConfiguration({ payload }) {
  const { request, userId } = payload;
  try {
    const { data: configuration } = yield call(restClient.request, request);
    yield put(actions.fetchConfigurationSuccess({ userId, configuration }));
  } catch (error) {
    yield put(actions.fetchConfigurationFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'notifications.navigation.notifications.patch.error',
          defaultMessage: 'An error has occurred during the settings notification update',
        },
      })
    );
  }
}

/** @param {import('./types').PatchNotificationsConfigurationAction} action */
function* patchConfiguration({ payload, meta }) {
  const { request, userId, notificationId } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    const { notificationConfig: configuration } = data;
    yield put(actions.patchConfigurationSuccess({ userId, notificationId, configuration }, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'notifications.navigation.notifications.patch.success',
          defaultMessage: 'Notification settings updated',
        },
      })
    );
  } catch (error) {
    yield put(actions.patchConfigurationFailure(error, notificationId, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'notifications.navigation.notifications.patch.error',
          defaultMessage: 'An error has occurred during the settings notification update',
        },
      })
    );
  }
}

/**
 * @param {import('./types').DisabledNotificationSettingQuicklyAction} action
 */
function* disabledNotificationSetting({ payload }) {
  const { extension, setting, notificationId } = payload;
  /** @type {import('./types').NotificationConfiguration} */
  const configuration = yield select(getUserNotificationConfig);
  const userId = yield select(getCurrentUserId);

  const filter = (channel) => ![CHANNELS.WEB, CHANNELS.DESKTOP].includes(channel);

  /** @type {import('./types').NotificationSettingDefinition} */
  const newData = {
    ...configuration[extension],
    [setting]: {
      ...configuration[extension][setting],
      channels: configuration[extension][setting]?.channels?.filter(filter) ?? [],
    },
  };

  yield put(
    actions.patchConfiguration({
      userId,
      notificationId,
      configuration: {
        ...configuration,
        [extension]: newData,
      },
    })
  );
}

function* acceptPermission() {
  const userId = yield select(getCurrentUserId);
  yield call([localStorage, localStorage.setItem], getPermissionDeniedKey(userId), false);
  yield put(actions.subscribe(userId));
}

function* refusePermission({ payload }) {
  const { userId } = payload;
  yield call([localStorage, localStorage.setItem], getPermissionDeniedKey(userId), true);
}

function* subscribeNotificationWorker({ payload: { userId } }) {
  const user = yield select(getCurrentUserAdditionalData);
  // We check `user` to avoid patch user during a sign out emergency
  if (userId && user && !user.notifiable) {
    yield put(patchUser(userId, 'notifiable', true));
  }
}

function* unsubscribeNotificationWorker({ payload: { userId } }) {
  const user = yield select(getCurrentUserAdditionalData);
  // We check `user` to avoid patch user during a sign out emergency
  if (userId && user && user.notifiable) {
    yield put(patchUser(userId, 'notifiable', false));
  }
}

const UNSUBSCRIBE_TYPES = [types.SUBSCRIBE_FAILURE, types.REFUSE_PERMISSION, types.NOTIFICATIONS_BLOCKED];

const FETCH_NOTIFICATIONS_TYPES = [types.FETCH_NOTIFICATIONS, types.FETCH_DROPDOWN_NOTIFICATIONS, types.FETCH_NOTIFICATIONS_DASHBOARD];

export default function* notificationsSagas() {
  yield takeLatest(types.SUBSCRIBE_REQUEST, subscribe);
  yield takeLatest(types.UNSUBSCRIBE_REQUEST, unsubscribe);
  yield takeLatest(types.SUBSCRIBE_SUCCESS, subscribeNotificationWorker);
  yield takeLatest(UNSUBSCRIBE_TYPES, unsubscribeNotificationWorker);

  yield takeLatest(FETCH_NOTIFICATIONS_TYPES, fetchNotifications);
  yield takeLatest(types.FETCH_UNREAD_NOTIFICATIONS_COUNT, fetchUnreadNotificationsCount);
  yield takeLatest(types.DELETE_NOTIFICATION, deleteNotification);
  yield takeLatest(types.MARK_AS_READ, markAsRead);
  yield takeLatest(types.MARK_AS_UNREAD, markAsUnread);
  yield takeLatest(types.MARK_ALL_AS_READ, markAllAsRead);

  yield takeLatest(types.FETCH_CONFIGURATION, fetchConfiguration);
  yield takeLatest(types.PATCH_CONFIGURATION, patchConfiguration);

  yield takeLatest(LOCATION_CHANGE, locationChanged);
  yield takeLatest(types.DISABLED_NOTIFICATION_SETTING, disabledNotificationSetting);

  yield takeLatest(types.ACCEPT_PERMISSION, acceptPermission);
  yield takeLatest(types.REFUSE_PERMISSION, refusePermission);
}
